import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { usePersistentState } from '../lib/storage';

export type Theme = 'light' | 'dark';
const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark';

function systemTheme(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  set: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Key must match the inline pre-paint script in index.html: my-hub:ui:theme
  const [theme, setTheme] = usePersistentState<Theme>('ui', 'theme', systemTheme(), isTheme);

  // Applied during RENDER, not in an effect, and this ordering is load-bearing.
  //
  // React runs a child's render AND its layout effects before the parent's
  // effects of either kind. So if this provider flipped the `dark` class in a
  // useEffect (or even a useLayoutEffect), any child that samples the themed
  // CSS custom properties while rendering — which is exactly what Recharts
  // panels do via chartColors() — would read the OUTGOING theme's values. If
  // that read is memoised on `theme` it never self-corrects, and the chart
  // stays permanently one toggle behind.
  //
  // Writing the class here, before any child renders, removes the window.
  // The write is idempotent, so a StrictMode double-render is harmless.
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }

  // Backstop: guarantees the COMMITTED theme wins even if a render above was
  // discarded, and keeps the DOM correct after hydration or a bailout.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), [setTheme]);

  const value = useMemo(() => ({ theme, toggle, set: setTheme }), [theme, toggle, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
